import PropTypes from "prop-types";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { shallow } from "zustand/shallow";

import useIntakeFormStore from "../../../store/IntakeForm.store";
import { handleInputPrefix } from "../../../utils/helpers/intakeForm";
import Card from "../../Card";
import SelectInput from "../../Form/SelectInput";
import TextInput from "../../Form/TextInput";
import {
  convertStringToNumber,
  isFundingSourceCashOnly,
} from "./../../../helpers";

const TaxInformationCard = ({ id, headerIcon, isEnabled }) => {
  const [formState, formOptions] = useIntakeFormStore(
    (state) => [state.data.formState, state.data.formOptions],
    shallow
  );

  const { control, setValue } = useFormContext();

  const minMax = (min, max) => {
    return {
      min: {
        value: min,
        message: `Min value is ${min}`,
      },
      max: {
        value: max,
        message: `Max value is ${max}`,
      },
    };
  };

  const isLocked = formState.isFormLocked;

  const isStrategyDirectIndexing = formState.strategyDetail
    ? formState.strategyDetail.sector === "Direct Indexing"
    : false;

  const enterNumberHelper = "Enter a number";
  const stTaxRateFieldValue = useWatch({
    control,
    name: "stTaxRate",
  });
  const ltTaxRateFieldValue = useWatch({
    control,
    name: "ltTaxRate",
  });
  const fundingSrc = useWatch({
    control,
    name: "fundingSrc",
  });

  const handlePercentageValue = (v) => {
    const val = isNaN(v) ? v.replace("%", "").replaceAll(",", "") : v;
    return val !== "" ? val : undefined;
  };

  const inputFields = [
    {
      type: "select",
      name: "taxState",
      label: "Select tax state",
      options: {
        required: false,
        readOnly: isLocked,
        items: formOptions.taxState || [],
      },
    },
    {
      type: "space",
    },
    {
      type: "select",
      name: "yearsToTransition",
      label: "Years to transition",
      options: {
        required: false,
        readOnly: isLocked,
        disabled: !isStrategyDirectIndexing,
        hidden: !isStrategyDirectIndexing,
        items: formOptions.yearsToTransition || [],
      },
    },
    {
      type: "text",
      name: "taxBudgetPerYear",
      label: "Tax Budget per Year",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(0, 10000000000),
        readOnly: isLocked,
        setValueAs: (v) => (v !== "" ? convertStringToNumber(v) : undefined),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
        },
        disabled: !isStrategyDirectIndexing,
        hidden: !isStrategyDirectIndexing,
      },
    },
    {
      type: "text",
      name: "stTaxRate",
      label: "Short term tax rate",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(0, 100),
        readOnly: isLocked,
        setValueAs: (v) => handlePercentageValue(v),
        masking: {
          numeral: true,
          numeralPositiveOnly: true,
          prefix: handleInputPrefix(stTaxRateFieldValue, "%"),
          tailPrefix: true,
          noImmediatePrefix: true,
        },
      },
    },
    {
      type: "text",
      name: "ltTaxRate",
      label: "Long term tax rate",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(0, 100),
        readOnly: isLocked,
        setValueAs: (v) => handlePercentageValue(v),
        masking: {
          numeral: true,
          numeralPositiveOnly: true,
          prefix: handleInputPrefix(ltTaxRateFieldValue, "%"),
          tailPrefix: true,
          noImmediatePrefix: true,
        },
      },
    },
    {
      type: "text",
      name: "stYtdRealizedL",
      label: "ST YTD realized loss",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(-10000000000, 10000000000),
        readOnly: isLocked,
        setValueAs: (v) => (v !== "" ? convertStringToNumber(v) : undefined),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
        },
      },
    },
    {
      type: "text",
      name: "ltYtdRealizedL",
      label: "LT YTD realized loss",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(0, 10000000000),
        readOnly: isLocked,
        setValueAs: (v) => (v !== "" ? convertStringToNumber(v) : undefined),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
        },
      },
    },
    {
      type: "text",
      name: "stYtdRealizedG",
      label: "ST YTD realized gain",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(0, 10000000000),
        readOnly: isLocked,
        setValueAs: (v) => (v !== "" ? convertStringToNumber(v) : undefined),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
        },
      },
    },
    {
      type: "text",
      name: "ltYtdRealizedG",
      label: "LT YTD realized gain",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(0, 10000000000),
        readOnly: isLocked,
        setValueAs: (v) => (v !== "" ? convertStringToNumber(v) : undefined),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
        },
      },
    },
    {
      type: "text",
      name: "maxRealizedGainShort",
      label: "Max realized short-term gains allowed (current tax year)",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(0, 10000000000),
        readOnly: isLocked,
        setValueAs: (v) => (v !== "" ? convertStringToNumber(v) : undefined),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
        },
      },
    },
    {
      type: "text",
      name: "maxRealizedGainLong",
      label: "Max realized long-term gains allowed (current tax year)",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(0, 10000000000),
        readOnly: isLocked,
        setValueAs: (v) => (v !== "" ? convertStringToNumber(v) : undefined),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
        },
      },
    },
    {
      type: "text",
      name: "carryFwdLosses",
      label: "Carry forward losses",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(-10000000000, 10000000000),
        readOnly: isLocked,
        setValueAs: (v) => (v !== "" ? convertStringToNumber(v) : undefined),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
        },
      },
    },
    {
      type: "text",
      name: "cashToRaise",
      label: "Cash to raise",
      options: {
        placeholder: enterNumberHelper,
        required: false,
        ...minMax(0, 10000000000),
        readOnly: isLocked,
        setValueAs: (v) => (v !== "" ? convertStringToNumber(v) : undefined),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
        },
        disabled: isFundingSourceCashOnly(fundingSrc),
      },
    },
  ];

  const tooltipProps = {
    heading: "Tax Information",
    content:
      "This is optional, but more information provides a more accurate transition analysis and plan.",
    direction: "top",
  };

  useEffect(() => {
    if (isFundingSourceCashOnly(fundingSrc)) {
      setValue("cashToRaise", "");
    }
  }, [fundingSrc, setValue]);

  return (
    <Card
      id={id}
      title="Client Tax Information"
      headerIcon={headerIcon}
      tooltip={tooltipProps}
      openByDefault={isEnabled}
      disabled={!isEnabled}
    >
      <div className="card__content">
        {inputFields.map((inputField, idx) => {
          if (inputField.type === "select") {
            return (
              <SelectInput
                key={idx}
                name={inputField.name}
                label={inputField.label}
                options={{ ...inputField.options }}
              />
            );
          } else if (inputField.type === "space") {
            return <div key={idx} />;
          }

          return (
            <TextInput
              key={idx}
              name={inputField.name}
              label={inputField.label}
              options={inputField.options}
            />
          );
        })}
      </div>
    </Card>
  );
};

TaxInformationCard.propTypes = {
  id: PropTypes.string,
  headerIcon: PropTypes.string,
  isEnabled: PropTypes.bool,
};

export default TaxInformationCard;
